import React, { useState, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Tooltip,
  Chip,
  CircularProgress,
  Alert,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import VerifiedIcon from '@mui/icons-material/Verified';
import PhoneIcon from '@mui/icons-material/Phone';
import { User, Location } from '../../../types/interfaces';
import { adminApi } from '../../../redux/adminApiSlice';
import DisplayJSON from '../../../components/DisplayJSON/DisplayJSON';
import ImagePreviewWithActions from '../../../components/Users/ImagePreviewWithActions';
import UserModal, { UserModalTab } from '../../../components/UserModal';
import { Spreadsheet } from '../../../spreadsheet/src';
import { ColumnDef, SortingState } from '@tanstack/react-table';
import { useCopyToClipboard, useFieldEditor, useDeleteWithConfirmation, useSpreadsheetConfig } from '../../../hooks';

// Extend User interface to include id for Spreadsheet
interface UserWithId extends User {
  id: string;
}

const getJoinedPlacesCount = (places: unknown): number => {
  if (typeof places === 'number') return places;
  if (Array.isArray(places)) return places.length;
  return 0;
};

// Stable module-level constant so the spreadsheet config memo isn't busted every render.
const FILTERABLE_COLUMNS = ['role', 'isAdmin', 'isMockUser', 'isOnline', 'displayName', 'phone'];

const getUserUniqueValue = (user: User, column: string): string => {
  switch (column) {
    case 'role':
      return user.role || 'user';
    case 'isAdmin':
      return user.isAdmin ? 'admin' : 'user';
    case 'isMockUser': {
      const isTest = user.email?.endsWith('@test.com');
      const isMock = user.isMockUser ? 'mock' : 'real';
      return isTest ? 'test' : isMock;
    }
    case 'isOnline':
      return user.isOnline ? 'online' : 'offline';
    case 'displayName': {
      const hasDisplayName = user.displayName && user.displayName.trim().length > 0;
      return hasDisplayName ? 'Display Name' : 'Empty Display Name';
    }
    case 'phone': {
      if (!user.phone) return 'No phone';
      return user.isPhoneVerified ? 'Phone verified' : 'Phone unverified';
    }
    default:
      return String(user[column as keyof User] || '');
  }
};

const userFilterFn = (user: User, filters: Record<string, string[]>): boolean => {
  // Role filter
  if (filters.role && filters.role.length > 0) {
    const role = user.role || 'user';
    if (!filters.role.includes(role)) return false;
  }

  // Admin filter
  if (filters.isAdmin && filters.isAdmin.length > 0) {
    const isAdmin = user.isAdmin ? 'admin' : 'user';
    if (!filters.isAdmin.includes(isAdmin)) return false;
  }

  // Mock User filter
  if (filters.isMockUser && filters.isMockUser.length > 0) {
    const isTest = user.email?.endsWith('@test.com');
    const isMock = user.isMockUser ? 'mock' : 'real';
    const userType = isTest ? 'test' : isMock;
    if (!filters.isMockUser.includes(userType)) return false;
  }

  // Online status filter
  if (filters.isOnline && filters.isOnline.length > 0) {
    const online = user.isOnline ? 'online' : 'offline';
    if (!filters.isOnline.includes(online)) return false;
  }

  // Display name filter
  if (filters.displayName && filters.displayName.length > 0) {
    const hasDisplayName = user.displayName && user.displayName.trim().length > 0;
    const displayNameStatus = hasDisplayName ? 'Display Name' : 'Empty Display Name';
    if (!filters.displayName.includes(displayNameStatus)) return false;
  }

  // Phone filter
  if (filters.phone && filters.phone.length > 0) {
    let phoneStatus: string;
    if (!user.phone) {
      phoneStatus = 'No phone';
    } else {
      phoneStatus = user.isPhoneVerified ? 'Phone verified' : 'Phone unverified';
    }
    if (!filters.phone.includes(phoneStatus)) return false;
  }

  return true;
};

const getProfilePicture = (url?: string) => {
  if (!url) return undefined;
  if (url.startsWith('http')) {
    return url;
  }
  if (url.startsWith('file://')) {
    return undefined;
  }
  return `http://localhost:3000/${url}`;
};

const AllUsersTab: React.FC = () => {
  const [selectedUserForJson, setSelectedUserForJson] = useState<User | null>(null);
  const [selectedUserForModal, setSelectedUserForModal] = useState<User | null>(null);
  const [userModalInitialTab, setUserModalInitialTab] = useState<UserModalTab>('info');
  const [previewImageUrl, setPreviewImageUrl] = useState<string | null>(null);
  const [previewImageUser, setPreviewImageUser] = useState<{ userId: string; photoType: 'facePicture' | 'publicPicture' } | null>(null);
  const { data: users = [], isLoading, error, refetch } = adminApi.useGetAllUsersQuery();
  const [deleteUser] = adminApi.useDeleteUserMutation();
  const [updateUser] = adminApi.useUpdateUserMutation();
  const [uploadFacePicture] = adminApi.useUploadUserFacePictureMutation();
  const [uploadPublicPicture] = adminApi.useUploadUserPublicPictureMutation();

  // Hooks
  const copyToClipboard = useCopyToClipboard();
  
  const fieldEditor = useFieldEditor<User>({
    onUpdate: async (id, field, value) => {
      const updates: Partial<User> = {};
      (updates as any)[field] = value;
      await updateUser({ id, updates }).unwrap();
    },
    onRefetch: async () => {
      await refetch();
    },
  });

  // Only the (stable) handleEditField callback is used inside cells. Depending on the
  // whole fieldEditor object would rebuild `columns` on every keystroke in the edit
  // dialog and every modal open, forcing a full table re-render.
  const { handleEditField } = fieldEditor;

  // Stable callbacks so useDeleteWithConfirmation returns a stable deleteItem,
  // which keeps `columns` (and therefore the whole table) from re-rendering.
  const handleDeleteUserRequest = useCallback(
    async (id: string) => {
      await deleteUser(id).unwrap();
    },
    [deleteUser]
  );
  const handleDeleteUserSuccess = useCallback(async () => {
    await refetch();
  }, [refetch]);

  const { deleteItem: handleDeleteUser } = useDeleteWithConfirmation({
    onDelete: handleDeleteUserRequest,
    onSuccess: handleDeleteUserSuccess,
    confirmationMessage: 'Are you sure you want to delete this user? This action cannot be undone.',
    errorMessage: 'Failed to delete user',
  });

  const handleDeletePhoto = useCallback(async () => {
    if (!previewImageUser) return;
    
    try {
      const updates: Partial<User> = {};
      if (previewImageUser.photoType === 'facePicture') {
        updates.facePicture = '';
        updates.profilePicture = '';
      } else {
        updates.publicPicture = '';
      }
      
      await updateUser({ id: previewImageUser.userId, updates }).unwrap();
      refetch();
    } catch (error) {
      console.error('Error deleting photo:', error);
      throw error;
    }
  }, [previewImageUser, updateUser, refetch]);

  const handleChangePhoto = useCallback(async (file: File) => {
    if (!previewImageUser) return;
    
    try {
      if (previewImageUser.photoType === 'facePicture') {
        await uploadFacePicture({ userId: previewImageUser.userId, photo: file }).unwrap();
      } else {
        await uploadPublicPicture({ userId: previewImageUser.userId, photo: file }).unwrap();
      }
      refetch();
    } catch (error) {
      console.error('Error changing photo:', error);
      throw error;
    }
  }, [previewImageUser, uploadFacePicture, uploadPublicPicture, refetch]);


  // Helper function to get location name
  // Backend now resolves location names, so this should receive objects with name property
  const getLocationName = useCallback((location: string | Location | undefined, userId?: string): string => {
    if (!location) return '-';
    
    // If it's an object with a name property, return the name (backend resolved it)
    if (typeof location === 'object' && 'name' in location && location.name) {
      return location.name;
    }
    
    // If it's a string, check if it looks like a MongoDB ObjectId
    if (typeof location === 'string') {
      // If it matches the user ID, it's likely a data error - show '-'
      if (userId && location === userId) {
        return '-';
      }
      // If it's a valid MongoDB ObjectId format (24 hex chars), backend should have resolved it
      // but if not, show '-' (shouldn't happen with the backend fix)
      if (/^[0-9a-fA-F]{24}$/.test(location)) {
        return '-'; // Backend should have resolved this, but fallback to '-'
      }
      // Otherwise, return the string as-is (might be a name stored directly)
      return location;
    }
    
    return '-';
  }, []);

  // Helper function to format date
  const formatDate = useCallback((date: string | Date | undefined): string => {
    if (!date) return '-';
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return dateObj.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }, []);

  // Helper function to format last active
  const formatLastActive = useCallback((date: string | Date | undefined): string => {
    if (!date) return 'Never';
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    const now = new Date();
    const diffMs = now.getTime() - dateObj.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return formatDate(date);
  }, [formatDate]);

  // Define columns (needs to be defined before spreadsheet config, so we'll move it)
  // Actually, we need columns first, so let's keep the order and update spreadsheet config
  const columns: ColumnDef<UserWithId>[] = useMemo(() => [
    {
      id: 'actions',
      header: 'Actions',
      size: 120,
      minSize: 100,
      maxSize: 140,
      enableSorting: false,
      cell: ({ row }) => {
        const user = row.original;
        return (
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Tooltip title="View User Details">
              <IconButton
                size="small"
                onClick={() => {
                  setUserModalInitialTab('info');
                  setSelectedUserForModal(user);
                }}
                color="primary"
              >
                <InfoIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete User">
              <IconButton
                size="small"
                onClick={() => handleDeleteUser(user._id)}
                color="error"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        );
      },
    },
    {
      id: 'facePicture',
      header: 'Face',
      size: 55,
      minSize: 50,
      maxSize: 60,
      enableSorting: false,
      cell: ({ row }) => {
        const user = row.original;
        const pictureUrl = getProfilePicture(user.facePicture || user.profilePicture);
        return (
          <Avatar
            src={pictureUrl}
            alt={user.displayName}
            sx={{ 
              width: 40, 
              height: 40,
              borderRadius: 1,
              cursor: 'pointer',
              '&:hover': { opacity: 0.8 }
            }}
            onClick={() => {
              if (pictureUrl) {
                setPreviewImageUrl(pictureUrl);
              } else {
                setPreviewImageUrl('');
              }
              setPreviewImageUser({ userId: user._id, photoType: 'facePicture' });
            }}
          />
        );
      },
    },
    {
      id: 'publicPicture',
      header: 'Public',
      size: 55,
      minSize: 50,
      maxSize: 60,
      enableSorting: false,
      cell: ({ row }) => {
        const user = row.original;
        const pictureUrl = getProfilePicture(user.publicPicture);
        return (
          <Avatar
            src={pictureUrl}
            alt={user.displayName}
            sx={{ 
              width: 40, 
              height: 40,
              borderRadius: 1,
              cursor: 'pointer',
              '&:hover': { opacity: 0.8 }
            }}
            onClick={() => {
              if (pictureUrl) {
                setPreviewImageUrl(pictureUrl);
              } else {
                setPreviewImageUrl('');
              }
              setPreviewImageUser({ userId: user._id, photoType: 'publicPicture' });
            }}
          />
        );
      },
    },
    {
      id: 'type',
      header: 'Type',
      size: 100,
      minSize: 80,
      maxSize: 120,
      enableSorting: true,
      accessorFn: (row) => {
        const user = row;
        const role = user.role || 'user';
        const isAdmin = user.isAdmin || role === 'admin';
        
        // If admin, return "Admin" for sorting
        if (isAdmin) {
          return 'Admin';
        }
        
        // Otherwise, return their type (Test, Mock, or Real) for alphabetical sorting
        const isMock = user.isMockUser;
        const isTest = user.email?.endsWith('@test.com');
        
        if (isTest) {
          return 'Test';
        } else if (isMock) {
          return 'Mock';
        } else {
          return 'Real';
        }
      },
      cell: ({ row }) => {
        const user = row.original;
        const role = user.role || 'user';
        const isAdmin = user.isAdmin || role === 'admin';
        
        // If admin, show Admin
        if (isAdmin) {
          return (
            <Chip
              label="Admin"
              size="small"
              color="error"
              variant="filled"
            />
          );
        }
        
        // Otherwise, show their type (Test, Mock, or Real)
        const isMock = user.isMockUser;
        const isTest = user.email?.endsWith('@test.com');
        
        let label: string;
        let color: 'warning' | 'success' | 'info';
        
        if (isTest) {
          label = 'Test';
          color = 'info';
        } else if (isMock) {
          label = 'Mock';
          color = 'warning';
        } else {
          label = 'Real';
          color = 'success';
        }
        
        return (
          <Chip
            label={label}
            size="small"
            color={color}
            variant="outlined"
          />
        );
      },
    },
    {
      accessorKey: 'displayName',
      header: 'Display Name',
      size: 180,
      minSize: 150,
      maxSize: 250,
      enableSorting: true,
      sortingFn: (rowA, rowB) => {
        const nameA = (rowA.getValue('displayName') as string) || '';
        const nameB = (rowB.getValue('displayName') as string) || '';
        
        // Treat blank names as if they come after Z
        const sortValueA = nameA.trim() || 'zzzzzzzzzz';
        const sortValueB = nameB.trim() || 'zzzzzzzzzz';
        
        return sortValueA.localeCompare(sortValueB, undefined, { sensitivity: 'base' });
      },
      cell: ({ getValue, row }) => {
        const name = getValue() as string;
        const user = row.original;
        return (
          <Typography
            variant="body2"
            noWrap
            title={name}
            onClick={() => handleEditField(user, 'displayName', 'Display Name')}
            sx={{
              cursor: 'pointer',
              '&:hover': { textDecoration: 'underline', color: 'primary.main' }
            }}
          >
            {name || '-'}
          </Typography>
        );
      },
    },
    {
      accessorKey: 'username',
      header: 'Username',
      size: 150,
      minSize: 120,
      maxSize: 200,
      enableSorting: true,
      cell: ({ row }) => {
        const username = row.original.username;
        const user = row.original;
        return (
          <Typography
            variant="body2"
            noWrap
            title={username}
            onClick={() => handleEditField(user, 'username', 'Username')}
            sx={{
              cursor: 'pointer',
              '&:hover': { textDecoration: 'underline', color: 'primary.main' }
            }}
          >
            @{username || '-'}
          </Typography>
        );
      },
    },
    {
      accessorKey: 'email',
      header: 'Email',
      size: 200,
      minSize: 180,
      maxSize: 250,
      enableSorting: true,
      cell: ({ getValue, row }) => {
        const email = getValue() as string;
        const user = row.original;
        return (
          <Typography
            variant="body2"
            noWrap
            title={email}
            onClick={() => handleEditField(user, 'email', 'Email')}
            sx={{
              cursor: 'pointer',
              '&:hover': { textDecoration: 'underline', color: 'primary.main' }
            }}
          >
            {email || '-'}
          </Typography>
        );
      },
    },
    {
      accessorKey: 'phone',
      header: 'Phone',
      size: 170,
      minSize: 140,
      maxSize: 220,
      enableSorting: true,
      accessorFn: (row) => (row.phone ? 'linked' : 'none'),
      cell: ({ row }) => {
        const user = row.original;
        const phone = user.phone;
        if (!phone) {
          return (
            <Chip
              label="Not linked"
              size="small"
              color="default"
              variant="outlined"
            />
          );
        }
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Tooltip title={user.isPhoneVerified ? 'Verified' : 'Unverified'}>
              {user.isPhoneVerified ? (
                <VerifiedIcon color="success" sx={{ fontSize: 16 }} />
              ) : (
                <PhoneIcon color="disabled" sx={{ fontSize: 16 }} />
              )}
            </Tooltip>
            <Typography
              variant="body2"
              noWrap
              title={phone}
              onClick={() => handleEditField(user, 'phone', 'Phone')}
              sx={{
                cursor: 'pointer',
                fontFamily: 'monospace',
                '&:hover': { textDecoration: 'underline', color: 'primary.main' },
              }}
            >
              {phone}
            </Typography>
            <IconButton size="small" onClick={() => copyToClipboard(phone)}>
              <ContentCopyIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Box>
        );
      },
    },
    {
      accessorKey: 'isOnline',
      header: 'Status',
      size: 100,
      minSize: 80,
      maxSize: 120,
      enableSorting: true,
      cell: ({ getValue }) => {
        const isOnline = getValue() as boolean;
        return (
          <Chip
            label={isOnline ? 'Online' : 'Offline'}
            size="small"
            color={isOnline ? 'success' : 'default'}
            variant={isOnline ? 'filled' : 'outlined'}
          />
        );
      },
    },
    {
      accessorKey: 'homeNeighborhood',
      header: 'Home',
      size: 150,
      minSize: 130,
      maxSize: 200,
      enableSorting: false,
      cell: ({ getValue, row }) => {
        const location = getValue() as string | Location | undefined;
        const user = row.original;
        const name = getLocationName(location, user._id);
        return (
          <Typography variant="body2" noWrap title={name}>
            {name}
          </Typography>
        );
      },
    },
    {
      accessorKey: 'currentNeighborhood',
      header: 'Neighb',
      size: 140,
      minSize: 110,
      maxSize: 200,
      enableSorting: false,
      cell: ({ getValue, row }) => {
        const location = getValue() as string | Location | undefined;
        const name = getLocationName(location, row.original._id);
        const display = !location || name === '-' ? '--' : name;
        return (
          <Typography variant="body2" noWrap title={display === '--' ? undefined : display}>
            {display}
          </Typography>
        );
      },
    },
    {
      accessorKey: 'currentCity',
      header: 'City',
      size: 140,
      minSize: 110,
      maxSize: 200,
      enableSorting: true,
      sortingFn: (rowA, rowB) => {
        const nameA = getLocationName(rowA.original.currentCity, rowA.original._id);
        const nameB = getLocationName(rowB.original.currentCity, rowB.original._id);
        const a = !rowA.original.currentCity || nameA === '-' ? '' : nameA.toLowerCase();
        const b = !rowB.original.currentCity || nameB === '-' ? '' : nameB.toLowerCase();
        if (!a && !b) return 0;
        if (!a) return 1;
        if (!b) return -1;
        return a.localeCompare(b);
      },
      cell: ({ getValue, row }) => {
        const location = getValue() as string | Location | undefined;
        const name = getLocationName(location, row.original._id);
        const display = !location || name === '-' ? '--' : name;
        return (
          <Typography variant="body2" noWrap title={display === '--' ? undefined : display}>
            {display}
          </Typography>
        );
      },
    },
    {
      accessorKey: 'friends',
      header: 'Friends',
      size: 80,
      minSize: 70,
      maxSize: 100,
      enableSorting: true,
      cell: ({ getValue }) => {
        const friends = getValue() as string[];
        return <Typography variant="body2" noWrap>{friends?.length || 0}</Typography>;
      },
    },
    {
      accessorKey: 'posts',
      header: 'Posts',
      size: 80,
      minSize: 70,
      maxSize: 100,
      enableSorting: true,
      cell: ({ getValue }) => {
        const posts = getValue() as string[] | number | undefined;
        // Handle both array (legacy) and number (new backend format)
        const count = typeof posts === 'number' ? posts : (Array.isArray(posts) ? posts.length : 0);
        return <Typography variant="body2" noWrap>{count}</Typography>;
      },
    },
    {
      accessorKey: 'joinedPlaces',
      header: 'Places',
      size: 80,
      minSize: 70,
      maxSize: 100,
      enableSorting: true,
      cell: ({ getValue, row }) => {
        const count = getJoinedPlacesCount(getValue());
        const user = row.original;
        return (
          <Tooltip title={count === 0 ? 'No joined places' : 'View joined places'}>
            <Button
              size="small"
              variant="text"
              onClick={(e) => {
                e.stopPropagation();
                setUserModalInitialTab('places');
                setSelectedUserForModal(user);
              }}
              sx={{
                minWidth: 0,
                px: 0.75,
                py: 0,
                textTransform: 'none',
                fontWeight: count > 0 ? 600 : 400,
                color: count > 0 ? 'primary.main' : 'text.secondary',
                textDecoration: count > 0 ? 'underline' : 'none',
              }}
            >
              {count}
            </Button>
          </Tooltip>
        );
      },
    },
    {
      accessorKey: 'lastActive',
      header: 'Last Active',
      size: 130,
      minSize: 120,
      maxSize: 150,
      enableSorting: true,
      sortingFn: (rowA, rowB) => {
        const dateA = rowA.getValue('lastActive') as string | Date | undefined;
        const dateB = rowB.getValue('lastActive') as string | Date | undefined;
        
        // Handle missing dates - put them at the end
        if (!dateA && !dateB) return 0;
        if (!dateA) return 1; // dateA goes after dateB
        if (!dateB) return -1; // dateB goes after dateA
        
        // Convert to Date objects and compare timestamps
        const dateObjA = typeof dateA === 'string' ? new Date(dateA) : dateA;
        const dateObjB = typeof dateB === 'string' ? new Date(dateB) : dateB;
        
        const timeA = dateObjA.getTime();
        const timeB = dateObjB.getTime();
        
        // Handle invalid dates
        if (isNaN(timeA) && isNaN(timeB)) return 0;
        if (isNaN(timeA)) return 1;
        if (isNaN(timeB)) return -1;
        
        return timeA - timeB; // Ascending: older dates first
      },
      cell: ({ getValue }) => {
        const date = getValue() as string | Date | undefined;
        const formatted = formatLastActive(date);
        return (
          <Typography variant="body2" noWrap title={date ? new Date(date).toLocaleString() : ''}>
            {formatted}
          </Typography>
        );
      },
    },
    {
      accessorKey: 'createdAt',
      header: 'Date Created',
      size: 130,
      minSize: 120,
      maxSize: 150,
      enableSorting: true,
      sortUndefined: 'last', // Always put undefined values at the bottom
      accessorFn: (row) => {
        // Convert missing/empty/null values to undefined so sortUndefined works
        const date = row.createdAt;
        if (!date || date === null || (typeof date === 'string' && date.trim() === '')) {
          return undefined;
        }
        return date;
      },
      sortingFn: (rowA, rowB) => {
        const dateA = rowA.getValue('createdAt') as string | Date | undefined | null;
        const dateB = rowB.getValue('createdAt') as string | Date | undefined | null;
        
        // If either is undefined, sortUndefined: 'last' will handle it
        if (dateA === undefined || dateB === undefined) {
          return 0; // Let sortUndefined handle it
        }
        
        // Convert to Date objects and compare timestamps
        const dateObjA = typeof dateA === 'string' ? new Date(dateA) : dateA;
        const dateObjB = typeof dateB === 'string' ? new Date(dateB) : dateB;
        
        if (!dateObjA || !dateObjB) {
          return 0; // Let sortUndefined handle null dates
        }
        
        const timeA = dateObjA.getTime();
        const timeB = dateObjB.getTime();
        
        // Handle invalid dates (treat as missing - sortUndefined will handle via accessorFn)
        if (isNaN(timeA) || isNaN(timeB)) {
          return 0; // Let sortUndefined handle invalid dates
        }
        
        return timeA - timeB;
      },
      cell: ({ getValue }) => {
        const date = getValue() as string | Date | undefined;
        const formatted = formatDate(date);
        return (
          <Typography variant="body2" noWrap title={date ? new Date(date).toLocaleString() : ''}>
            {formatted}
          </Typography>
        );
      },
    },
    {
      accessorKey: '_id',
      header: 'ID',
      size: 140,
      minSize: 120,
      maxSize: 160,
      enableSorting: true,
      cell: ({ getValue }) => {
        const id = getValue() as string;
        const shortId = id.substring(0, 8) + '...';
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Tooltip title={id}>
              <Typography variant="body2" noWrap sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                {shortId}
              </Typography>
            </Tooltip>
            <IconButton size="small" onClick={() => copyToClipboard(id)}>
              <ContentCopyIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Box>
        );
      },
    },
  ], [handleDeleteUser, copyToClipboard, handleEditField, formatLastActive, formatDate, getLocationName, setPreviewImageUrl, setPreviewImageUser]);

  // Initial sort state - sort by createdAt descending (most recent first)
  const [sorting, setSorting] = useState<SortingState>([
    { id: 'createdAt', desc: true }
  ]);

  // Spreadsheet configuration with custom filter logic.
  // getUniqueValue/filterFn/filterableColumns are stable (module-level) so the
  // config memo and unique-value scan don't re-run on every render.
  const spreadsheet = useSpreadsheetConfig<User>({
    data: users,
    columns: columns as any,
    filterableColumns: FILTERABLE_COLUMNS,
    enableFiltering: true,
    getUniqueValue: getUserUniqueValue,
    filterFn: userFilterFn,
  });

  const spreadsheetProps = spreadsheet.spreadsheetProps;

  // Memoize the table element so opening the user modal / image preview (unrelated
  // state) does not re-render the entire (potentially huge) spreadsheet.
  const spreadsheetElement = useMemo(
    () => (
      <Spreadsheet
        {...spreadsheetProps}
        sorting={sorting}
        onSortingChange={setSorting}
      />
    ),
    [spreadsheetProps, sorting]
  );

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    let errorMessage = 'An unknown error occurred';
    if (typeof error === 'object' && error !== null) {
      if ('data' in error && error.data && typeof error.data === 'object' && 'message' in error.data) {
        errorMessage = String(error.data.message);
      } else if ('error' in error) {
        errorMessage = String(error.error);
      }
    }
    
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="h6" gutterBottom>Error loading users</Typography>
          <Typography variant="body2">{errorMessage}</Typography>
        </Alert>
        <Button 
          variant="outlined" 
          startIcon={<RefreshIcon />}
          onClick={() => refetch()}
        >
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      width: '100%', 
      maxWidth: '100%', 
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      boxSizing: 'border-box',
      p: 2,
    }}>
      {/* Header */}
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
        <Box>
          <Typography variant="h5">All Users</Typography>
          <Typography variant="body2" color="text.secondary">
            {spreadsheet.filteredData.length} of {users.length} users
            {users.filter(u => u.isMockUser).length > 0 && (
              <> • {users.filter(u => u.isMockUser).length} mock users</>
            )}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={() => refetch()}
          size="small"
        >
          Refresh
        </Button>
      </Box>

      {/* Spreadsheet */}
      <Box sx={{ flex: 1, width: '100%', maxWidth: '100%', minHeight: 0 }}>
        {spreadsheetElement}
      </Box>

      {/* User Modal */}
      <UserModal
        isOpen={!!selectedUserForModal}
        onClose={() => {
          setSelectedUserForModal(null);
          setUserModalInitialTab('info');
        }}
        user={selectedUserForModal}
        initialTab={userModalInitialTab}
        onUserUpdate={(updatedUser) => {
          // Refetch users list to show updated data
          refetch();
        }}
      />

      {/* JSON Display Dialog - Keep for backward compatibility if needed */}
      {selectedUserForJson && (
        <DisplayJSON
          open={true}
          onClose={() => setSelectedUserForJson(null)}
          data={selectedUserForJson}
          title="User JSON Details"
          subtitle={selectedUserForJson.displayName || selectedUserForJson.username}
        />
      )}

      {/* Image Preview Modal */}
      {previewImageUser && (
        <ImagePreviewWithActions
          open={true}
          imageUrl={previewImageUrl || ''}
          onClose={() => {
            setPreviewImageUrl(null);
            setPreviewImageUser(null);
          }}
          onDelete={previewImageUrl ? handleDeletePhoto : undefined}
          onChange={handleChangePhoto}
          photoType={previewImageUser.photoType}
        />
      )}

      {/* Edit Field Modal */}
      <Dialog
        open={!!fieldEditor.editingField}
        onClose={fieldEditor.handleCloseEditModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Edit {fieldEditor.editingField?.label}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            <TextField
              label={fieldEditor.editingField?.label}
              value={fieldEditor.editValue}
              onChange={(e) => fieldEditor.setEditValue(e.target.value)}
              fullWidth
              multiline={fieldEditor.editingField?.field === 'bio'}
              rows={fieldEditor.editingField?.field === 'bio' ? 4 : 1}
              autoFocus
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={fieldEditor.handleDeleteField}
            color="error"
            disabled={fieldEditor.isSaving || !fieldEditor.editValue}
            startIcon={fieldEditor.isSaving ? <CircularProgress size={16} /> : <DeleteIcon />}
          >
            Delete
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button onClick={fieldEditor.handleCloseEditModal} disabled={fieldEditor.isSaving}>
            Cancel
          </Button>
          <Button
            onClick={fieldEditor.handleSaveField}
            variant="contained"
            disabled={fieldEditor.isSaving}
            startIcon={fieldEditor.isSaving ? <CircularProgress size={16} /> : null}
          >
            {fieldEditor.isSaving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AllUsersTab;
